import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Form,
  FormGroup,
  Label,
  CardFooter,
  Input,
  Collapse,
  Spinner,
} from "reactstrap";
import { withRouter } from "react-router";
import swal from "sweetalert2";
import Photo from "@/views/Components/Photo";

import { dateFormatter } from "@/util/formatter";
import { getStoredItem, setStoredItem } from "@/store/dataStore";
import { LIMITS_STORAGE_NAME } from "@/store/constants";
import limitsApi from "@/api/limits";
import verificationApi from "@/api/verification";
import usersApi from "@/api/users";

import { handleError } from "@/util/errorHandler";
import KeyRecoveryModal from "@/views/Components/KeyRecoveryRequest";
import GenerateQrModal from "@/views/Components/QrCode/GenerateQrModal";
import UpdateAccountModal from "@/views/Components/UpdateAccount/UpdateAccountModal";
import PrepareRecoveryModal from "@/views/Components/PrepareRecoveryModal/PrepareRecoveryModal";
import TableRow from "../Components/DnDTable/TableRow";
import TableHeaderCell from "../Components/DnDTable/TableHeaderCell";
import ChangePhoneModal from "@/views/Components/ChangePhoneModal";

class CustomerInfoCom extends Component {
  static propTypes = {
    data: PropTypes.object,
    onApprove: PropTypes.func,
    onReject: PropTypes.func,
    updateAccount: PropTypes.func,
    approvable: PropTypes.bool,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
  };

  static defaultProps = {
    data: {},
    onApprove: () => {},
    onReject: () => {},
    updateAccount: () => {},
    approvable: false,
  };

  constructor(props) {
    super(props);

    // Using localStorage instead of global state manager 😄
    const limitsList = getStoredItem(LIMITS_STORAGE_NAME);

    this.state = {
      limitsList: limitsList ? JSON.parse(limitsList) : [],
      selectedUserLimit: undefined,
      isDocsVisible: false,
      isDocsLoading: false,
      docs: "",
      photo: "",
      isPrepareRecoveryOpen: false,
      isKeyRecoveryOpen: false,
      isGenerateQrModalOpen: false,
      isUpdateAccountModalOpen: false,
      isChangePhoneModalOpen: false,
      updateHistory: [],
      identityHistory: [],
    };
  }

  componentDidMount() {
    if (this.props.type !== "auditor") {
      this.loadOrTakeFromCacheLimitsList();
    }
    this.loadPhotos();
  }

  componentDidUpdate(prevProps) {
    const { kycLimitId, identityUuid, photoUuid, accountId } = this.props.data;
    const isAuditor = this.props.type === "auditor";

    // Updating selected user limit because props updates asynchronously by api from parent component
    // Just payment for bad design of whole app
    if (prevProps.data.kycLimitId !== kycLimitId) {
      this.setState({ selectedUserLimit: kycLimitId });
    }

    if (
      prevProps.data.identityUuid !== identityUuid ||
      prevProps.data.photoUuid !== photoUuid
    ) {
      this.loadPhotos();
    }

    if (prevProps.data.accountId !== accountId && !isAuditor) {
      this.loadUserHistory(accountId);
      this.loadUserVerificationHistory(accountId);
    }
  }

  onLimitChange = ({ target: { value } }) => {
    this.setState({ selectedUserLimit: value });
  };

  getStatusIndicator = () => {
    const { selectedUserLimit, limitsList } = this.state;

    const {
      data: { accountType },
    } = this.props;

    const limitsOptionsForSelectedUserType = limitsList
      .filter(l => l.accountType === accountType)
      .map(l => (
        <option key={l.id} value={l.id}>
          {l.limitName}
        </option>
      ));

    return (
      <Row className="pb-5">
        <Col xs="10" sm="10" md={{ size: 6, offset: 3 }}>
          <Input
            type="select"
            value={selectedUserLimit}
            onChange={this.onLimitChange}>
            {limitsOptionsForSelectedUserType}
          </Input>
        </Col>
        <Col xs="2" sm="2" md="3">
          {this.props.data.kycLimitId != this.state.selectedUserLimit &&
            !this.props.approvable && (
              <Button
                color="success"
                type="button"
                onClick={this.onChangeUserLimit}>
                Save
              </Button>
            )}
        </Col>
      </Row>
    );
  };

  getFooterView = () => {
    const { onApprove, onReject, approvable } = this.props;
    const { selectedUserLimit } = this.state;

    if (approvable) {
      return (
        <CardFooter className="d-flex justify-content-center">
          <Button
            color="danger"
            type="button"
            className="mr-4"
            onClick={onReject}>
            REJECT
          </Button>
          <Button
            color="success"
            type="button"
            onClick={() => onApprove(selectedUserLimit)}>
            APPROVE
          </Button>
        </CardFooter>
      );
    }
  };

  loadOrTakeFromCacheLimitsList = () => {
    limitsApi
      .getLimitsListAll()
      .then(limitsList => {
        setStoredItem(LIMITS_STORAGE_NAME, JSON.stringify(limitsList));
        this.setState({ limitsList });
      })
      .catch(err => handleError(err));
  };

  loadPhotos = async () => {
    const { identityUuid, photoUuid } = this.props.data || {};

    if (identityUuid || photoUuid) {
      try {
        this.setState({ isDocsLoading: true });

        await Promise.all([
          this.loadIdentityPhoto(identityUuid),
          this.loadUserPhoto(photoUuid),
        ]);
      } catch (err) {
        console.error(err);
        if (err.status !== 404 && err.data) {
          handleError(err.data);
        }
        // it's fine to ignore error if status is 404
      } finally {
        this.setState({ isDocsLoading: false });
      }
    }
  };

  loadIdentityPhoto = async identityUuid => {
    // don't load photo if uuid is empty
    if (!identityUuid) return;

    const docs = await verificationApi.getDocumentsPhoto(identityUuid);
    this.setState({ docs });
  };

  loadUserPhoto = async photoUuid => {
    // don't load photo if uuid is empty
    if (!photoUuid) return;

    const photo = await verificationApi.getUserPhoto(photoUuid);
    this.setState({ photo });
  };

  loadUserHistory = async accountId => {
    if (!accountId) return;

    const updateHistory = await verificationApi.getUserHistoryByAccountId(
      accountId,
    );
    this.setState({ updateHistory });
  };

  loadUserVerificationHistory = async accountId => {
    if (!accountId) return;

    const identityHistory = await verificationApi.getUserIdentityHistory(
      accountId,
    );
    this.setState({ identityHistory });
  };

  isFrozen = () => this.props.data && this.props.data.frozen;

  onToggleFreezeAccount = () => {
    const isFrozen = this.isFrozen();
    swal({
      title: "Are you sure?",
      text: isFrozen
        ? "Do you want to unblock this account?"
        : "Do you want to block this account?",
      type: "warning",
      showCancelButton: true,
      confirmButtonText: isFrozen ? "Unblock" : "Block",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then(async result => {
      if (result.value)
        try {
          if (isFrozen) {
            await usersApi.unfreezeAccount(this.props.data.accountId);
            swal("Success", "Account unblocked", "success");
          } else {
            await usersApi.freezeAccount(this.props.data.accountId);
            swal("Success", "Account blocked", "success");
          }
        } catch (error) {
          handleError(error);
        } finally {
          await this.props.updateAccount();
        }
    });
  };

  toggleDocsVisibility = () =>
    this.setState(({ isDocsVisible }) => ({ isDocsVisible: !isDocsVisible }));

  onChangeUserLimit = () => {
    const { data, updateAccount } = this.props;

    limitsApi
      .changeLimitForUser({
        accountId: this.props.data.id,
        limitId: this.state.selectedUserLimit,
      })
      .then(() =>
        swal(
          "Success",
          `Limit for user ${data.fullName} has been changed successfully`,
          "success",
        ).then(() => {
          updateAccount();
        }),
      )
      .catch(err => handleError(err));
  };

  onTogglePrepareRecoveryModal = () => {
    this.setState(({ isPrepareRecoveryOpen }) => ({
      isPrepareRecoveryOpen: !isPrepareRecoveryOpen,
    }));
  };

  onToggleKeyRecoveryModal = () => {
    this.setState(({ isKeyRecoveryOpen }) => ({
      isKeyRecoveryOpen: !isKeyRecoveryOpen,
    }));
  };

  onPrepareSuccess = () => {
    this.onTogglePrepareRecoveryModal();
    this.onToggleKeyRecoveryModal();
  };

  onToggleGenerateQrModal = () => {
    this.setState(({ isGenerateQrModalOpen }) => ({
      isGenerateQrModalOpen: !isGenerateQrModalOpen,
    }));
  };

  onToggleUpdateAccountModal = () => {
    const { updateAccount } = this.props;
    this.setState(({ isUpdateAccountModalOpen }) => {
      if (isUpdateAccountModalOpen) {
        updateAccount();
        this.loadUserHistory(this.props.data.accountId);
      }
      return {
        isUpdateAccountModalOpen: !isUpdateAccountModalOpen,
      };
    });
  };

  onToggleChangePhoneModal = () => {
    this.setState(({ isChangePhoneModalOpen }) => ({
      isChangePhoneModalOpen: !isChangePhoneModalOpen,
    }));
  };

  displayPhotos = accountType => {
    if (this.state.isDocsLoading) {
      return <Spinner color="danger" />;
    }

    return (
      <Row>
        <Col sm="6">
          <Photo
            src={this.state.photo}
            name="User photo"
            data-cy="user-photo"
          />
        </Col>
        {accountType !== "DESKTOP" && (
          <Col sm="6">
            <Photo
              src={this.state.docs}
              name="Identity photo"
              data-cy="identity-photo"
            />
          </Col>
        )}
      </Row>
    );
  };

  displayManageAuthorizersButton = ({ accountType, accountId }) => {
    if (accountType !== "DESKTOP") return null;

    return (
      <FormGroup row className="mb-4">
        <Col md="3">
          <Label>Authorizers:</Label>
        </Col>
        <Col xs="12" md="9">
          <Button
            color="secondary"
            onClick={() =>
              this.props.history.push(`/manage_authorizers/${accountId}`)
            }>
            Manage authorizers
          </Button>
        </Col>
      </FormGroup>
    );
  };

  render() {
    const {
      history,
      data: {
        accountType,
        fullName,
        organizationName,
        accountId,
        phoneNumber,
        email,
        address,
        registeredDate,
        createdTime,
        requestedDate,
        birthday,
        sex,
        nationality,
        documentId,
        status,
        kycLimitId,
        kycStatus,
      },
      type,
    } = this.props;

    const isAuditor = type === "auditor";

    const { updateHistory, identityHistory, limitsList } = this.state;
    const currentLimit = limitsList.find(l => l.id === kycLimitId);

    const accountHistoryColumns = [
      {
        title: "Field",
        dataField: "fieldName",
        style: { width: "15%" },
      },
      {
        title: "Old value",
        dataField: "oldValue",
      },
      {
        title: "New value",
        dataField: "currentValue",
      },
      {
        title: "Changed by",
        dataField: "changedBy",
        style: { width: "20%" },
      },
      {
        title: "Date",
        dataField: "dateTime",
        style: { width: "20%" },
      },
    ];

    const identityHistoryColumns = [
      {
        title: "Live confidence %",
        dataField: "liveConfidenceAvg",
        style: { width: "15%" },
      },
      {
        title: "Liveness",
        dataField: "live",
      },
      {
        title: "Face match",
        dataField: "faceMatch",
      },
      {
        title: "Changed by",
        dataField: "changedBy",
        style: { width: "20%" },
      },
      {
        title: "Date",
        dataField: "dateTime",
        style: { width: "20%" },
      },
    ];

    return (
      <>
        <Card>
          <CardHeader>User Info</CardHeader>
          <CardBody>
            {!isAuditor && this.getStatusIndicator()}
            <Row className="justify-content-center">
              <Col>
                <Form className="form-horizontal">
                  {accountType === "MOBILE" && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Full Name:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>{fullName}</Label>
                      </Col>
                    </FormGroup>
                  )}
                  <FormGroup row>
                    <Col md="3">
                      <Label>KYC status:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Label>
                        {kycStatus}{" "}
                        {currentLimit && <>({currentLimit.limitName})</>}
                      </Label>
                    </Col>
                  </FormGroup>
                  {accountType === "DESKTOP" && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Organisation:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>{organizationName}</Label>
                      </Col>
                    </FormGroup>
                  )}
                  <FormGroup row>
                    <Col md="3">
                      <Label>Account ID:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Label>{accountId}</Label>
                    </Col>
                  </FormGroup>
                  <FormGroup row>
                    <Col md="3">
                      <Label>Status:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Label>{status}</Label>
                    </Col>
                  </FormGroup>
                  {accountType === "MOBILE" && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Phone Number:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>{phoneNumber}</Label>
                        {!isAuditor && (
                          <Button
                            style={{ marginLeft: "1rem" }}
                            color="warning"
                            onClick={this.onToggleChangePhoneModal}>
                            <span>Change phone number</span>
                          </Button>
                        )}
                      </Col>
                    </FormGroup>
                  )}
                  <FormGroup row>
                    <Col md="3">
                      <Label>Email:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Label>{email}</Label>
                    </Col>
                  </FormGroup>
                  <FormGroup row className="mb-4">
                    <Col md="3">
                      <Label>Address:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Label>{address}</Label>
                    </Col>
                  </FormGroup>
                  {accountType === "MOBILE" && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Gender:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>{sex || "unknown"}</Label>
                      </Col>
                    </FormGroup>
                  )}
                  {accountType === "MOBILE" && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Nationality:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>{nationality || "unknown"}</Label>
                      </Col>
                    </FormGroup>
                  )}
                  {accountType === "MOBILE" && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Document ID:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>{documentId || "unknown"}</Label>
                      </Col>
                    </FormGroup>
                  )}
                  {accountType === "MOBILE" && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Birth date:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>
                          {dateFormatter(birthday, {
                            showTime: false,
                            dateFormat: "array",
                          })}
                        </Label>
                      </Col>
                    </FormGroup>
                  )}
                  <FormGroup row className="mb-4">
                    <Col md="3">
                      <Label>Registration date:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Label>
                        {dateFormatter(registeredDate || createdTime)}
                      </Label>
                    </Col>
                  </FormGroup>
                  {requestedDate && (
                    <FormGroup row className="mb-4">
                      <Col md="3">
                        <Label>Request Date:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>{dateFormatter(requestedDate)}</Label>
                      </Col>
                    </FormGroup>
                  )}
                  <FormGroup row className="mb-4">
                    <Col md="3">
                      <Label>Documents:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Button
                        color="primary"
                        onClick={this.toggleDocsVisibility}
                        data-cy="show-photos">
                        {this.state.isDocsVisible ? "Hide" : "Show"}
                      </Button>
                      <Collapse isOpen={this.state.isDocsVisible}>
                        <Card style={{ marginTop: 5 }}>
                          <CardBody style={{ width: "100%" }}>
                            {this.displayPhotos(accountType)}
                          </CardBody>
                        </Card>
                      </Collapse>
                    </Col>
                  </FormGroup>
                  {!isAuditor ? (
                    accountType === "MOBILE" ? (
                      <FormGroup row className="mb-4">
                        <Col md="3">
                          <Label>Prepare key recovery:</Label>
                        </Col>
                        <Col xs="12" md="9">
                          <Button
                            color="warning"
                            onClick={this.onTogglePrepareRecoveryModal}>
                            <i className="fa fa-key mr-2" />
                            <span>Prepare key recovery procedure</span>
                          </Button>
                        </Col>
                      </FormGroup>
                    ) : (
                      <FormGroup row className="mb-4">
                        <Col md="3">
                          <Label>Key recovery:</Label>
                        </Col>
                        <Col xs="12" md="9">
                          <Button
                            color="warning"
                            onClick={this.onToggleKeyRecoveryModal}>
                            <i className="fa fa-key mr-2" />
                            <span>Start key recovery procedure</span>
                          </Button>
                        </Col>
                      </FormGroup>
                    )
                  ) : (
                    ""
                  )}
                  {!isAuditor && (
                    <FormGroup row className="mb-4">
                      <Col md="3">
                        <Label>QR code:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Button
                          color="warning"
                          onClick={this.onToggleGenerateQrModal}>
                          <span>Generate QR code</span>
                        </Button>
                      </Col>
                    </FormGroup>
                  )}
                  {!isAuditor && accountType === "MOBILE" && (
                    <FormGroup row className="mb-4">
                      <Col xs="12" md="9">
                        <Button
                          color="success"
                          onClick={this.onToggleUpdateAccountModal}>
                          <span>Open update account modal</span>
                        </Button>
                      </Col>
                    </FormGroup>
                  )}
                  {!isAuditor && (
                    <FormGroup row className="mb-4">
                      <Col xs="12" md="9">
                        <Button
                          color="success"
                          onClick={this.onToggleFreezeAccount}>
                          <span>
                            {this.isFrozen()
                              ? "Unblock account"
                              : "Block account"}
                          </span>
                        </Button>
                      </Col>
                    </FormGroup>
                  )}

                  {!isAuditor &&
                    this.displayManageAuthorizersButton({
                      accountType,
                      accountId,
                    })}
                </Form>
                <Button
                  type="primary"
                  onClick={() => history.goBack()}
                  className="float-right"
                  data-cy="back">
                  <i className="fa fa-arrow-left mr-2" />
                  Back
                </Button>
              </Col>
            </Row>
          </CardBody>
          {!isAuditor && this.getFooterView()}
        </Card>

        {!isAuditor && accountType === "MOBILE" && (
          <Card>
            <CardHeader>Profile history</CardHeader>
            <CardBody>
              <table className="table table-hover table-bordered table-sm dnd-table">
                <thead>
                  <tr className="dnd-table-header">
                    {accountHistoryColumns.map(column => (
                      <TableHeaderCell
                        key={column.title}
                        title={column.title}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {updateHistory.map((row, i) => (
                    <TableRow
                      key={i}
                      row={row}
                      columns={accountHistoryColumns}
                      isDragging={false}
                    />
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>
        )}

        {!isAuditor && accountType === "MOBILE" && (
          <Card>
            <CardHeader>Verification history</CardHeader>
            <CardBody>
              <table className="table table-hover table-bordered table-sm dnd-table">
                <thead>
                  <tr className="dnd-table-header">
                    {identityHistoryColumns.map(column => (
                      <TableHeaderCell
                        key={column.title}
                        title={column.title}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {identityHistory.map((row, i) => (
                    <TableRow
                      key={i}
                      row={row}
                      columns={identityHistoryColumns}
                      isDragging={false}
                    />
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>
        )}

        <PrepareRecoveryModal
          isOpen={this.state.isPrepareRecoveryOpen}
          onModalClose={this.onTogglePrepareRecoveryModal}
          onSuccess={this.onPrepareSuccess}
          userData={{ accountId, fullName }}
        />

        <KeyRecoveryModal
          isOpen={this.state.isKeyRecoveryOpen}
          onModalClose={this.onToggleKeyRecoveryModal}
          userData={{ accountId, fullName, documentId }}
          organization={accountType === "DESKTOP" ? organizationName : null}
        />

        <GenerateQrModal
          isOpen={this.state.isGenerateQrModalOpen}
          onModalClose={this.onToggleGenerateQrModal}
          userAccountId={accountId}
          organizationName={organizationName}
        />

        <UpdateAccountModal
          isOpen={this.state.isUpdateAccountModalOpen}
          onModalClose={this.onToggleUpdateAccountModal}
          data={this.props.data}
        />

        <ChangePhoneModal
          isOpen={this.state.isChangePhoneModalOpen}
          onModalClose={this.onToggleChangePhoneModal}
          userAccountId={accountId}
        />
      </>
    );
  }
}

export default withRouter(CustomerInfoCom);
